import React from 'react'
import {log} from '@/logger'
import {createContextHelper} from '../../../utils/createContextHelper'
import {api} from '../../../services/api'
import {useRequiredAuth} from '../../../stores/AuthStore'
import {ResourceID, CamperSegment, SegmentCurrentSnapshot} from '../../../types'
import {allArraysAreEmpty} from '../../../utils'
import {SegmentType} from '../../../services/coreApi/segments/types'

const CAMPER_PARENT_SEGMENT_TYPE: SegmentType = 'Camper/Parent'

/* ***************************************************** */
type CreatePayload = {segmentID: string | null} | null

type ReturnStatus = 'success' | 'failure'

// Tracks the leader's explicit audience choice. 'unset' means they have not
// yet decided and Publish should be blocked. Set on every save path.
export type AudienceMode = 'unset' | 'everyone' | 'custom'

interface SegmentManagerContext extends ReducerState {
  mode: 'edit' | 'create'
  dispatch: React.Dispatch<Action>
  createSegment(): Promise<{
    status: ReturnStatus
    data: CreatePayload
  }>
  deleteSegment(segmentID?: string | null): Promise<{
    status: ReturnStatus
  }>
  getSegmentCurrentSnapShot(segmentID: ResourceID): Promise<{
    status: ReturnStatus
    data: SegmentCurrentSnapshot | null
  }>
}

const [SegmentManagerContextProvider, useSegmentManagerContext] =
  createContextHelper<SegmentManagerContext>()

/* ***************************************************** */
interface ReducerState {
  status: 'idle' | 'pending' | 'rejected' | 'owned' | 'unowned'
  segments: CamperSegment
  // Internal-only: backs RESET so the segment step can revert to the
  // last-applied filter set. Not consumed by any component that reads
  // the SegmentManager context.
  storedSegments: CamperSegment
  segmentID: ResourceID | null
  audienceMode: AudienceMode
}

type Action =
  | {type: 'FETCH_INIT'}
  | {
      type: 'FETCH_SUCCESS'
      payload: {
        data: CamperSegment
      }
    }
  | {type: 'FETCH_FAILURE'}
  | {type: 'CREATE_INIT'}
  | {
      type: 'CREATE_SUCCESS'
      payload: {
        segmentID: ResourceID | null
      }
    }
  | {type: 'CREATE_FAILURE'}
  | {
      type: 'UPDATE_SEGMENT_FILTER'
      payload: {
        filter: keyof CamperSegment
        values: CamperSegment[keyof CamperSegment]
      }
    }
  | {type: 'RESET'}
  | {type: 'SET_AUDIENCE_EVERYONE'}

const emptySegment: CamperSegment = {
  genderIDs: [],
  sessionIDs: [],
  divisionIDs: [],
  programIDs: [],
  bunkIDs: [],
  bunkPlanIDs: [],
}

const reducer: React.Reducer<ReducerState, Action> = (state, action) => {
  switch (action.type) {
    case 'FETCH_INIT':
    case 'CREATE_INIT': {
      return {
        ...state,
        status: 'pending',
      }
    }
    case 'FETCH_SUCCESS': {
      return {
        ...state,
        segments: action.payload.data,
        storedSegments: action.payload.data,
        status: 'owned',
      }
    }
    case 'FETCH_FAILURE':
    case 'CREATE_FAILURE': {
      return {
        ...state,
        status: 'rejected',
      }
    }
    case 'CREATE_SUCCESS': {
      // Apply succeeded on the custom segment step. createSegment refuses
      // to call the API when every filter is empty, and upsertSegment
      // always returns a non-null ID on success, so reaching this case
      // means audienceMode is 'custom'.
      return {
        ...state,
        status: 'unowned',
        segmentID: action.payload.segmentID,
        storedSegments: state.segments,
        audienceMode: 'custom',
      }
    }
    case 'UPDATE_SEGMENT_FILTER': {
      return {
        ...state,
        segments: {
          ...state.segments,
          [action.payload.filter]: action.payload.values,
        },
      }
    }
    case 'RESET': {
      return {
        ...state,
        segments: state.storedSegments,
      }
    }
    case 'SET_AUDIENCE_EVERYONE': {
      // Caller is responsible for deleting any prior unowned segment before
      // dispatching; reset status back to 'idle' so Modal close cleanup
      // doesn't fire against a stale (already-deleted) segmentID.
      return {
        ...state,
        status: 'idle',
        audienceMode: 'everyone',
        segments: emptySegment,
        storedSegments: emptySegment,
        segmentID: null,
      }
    }
    default:
      throw new Error('Unhandled action')
  }
}

/* ***************************************************** */
interface SegmentManagerProps {
  initial: {
    segmentID?: string | null
  }
}

const initialAudienceMode = (
  segmentID: string | null | undefined,
): AudienceMode => {
  if (segmentID === undefined) return 'unset' // create flow
  return segmentID === null ? 'everyone' : 'custom' // edit flow reflects stored state
}

export const SegmentManager: React.FC<
  React.PropsWithChildren<SegmentManagerProps>
> = ({initial, children}) => {
  // Treat an empty-string ID the same as null so audienceMode and
  // segmentID can never disagree: otherwise '' would set audienceMode
  // to 'custom' while the persisted segmentID became null, leaving the
  // UI promising a custom segment that the payload doesn't carry.
  // undefined is preserved so initialAudienceMode can distinguish the
  // create flow ('unset') from the edit-as-everyone flow (null).
  const normalizedSegmentID =
    initial.segmentID === '' ? null : initial.segmentID
  const [state, dispatch] = React.useReducer(reducer, {
    status: 'idle',
    storedSegments: emptySegment,
    segments: emptySegment,
    segmentID: normalizedSegmentID ?? null,
    audienceMode: initialAudienceMode(normalizedSegmentID),
  })
  const {client} = useRequiredAuth()

  React.useEffect(() => {
    let didCancel = false

    const fetchData = async (): Promise<void> => {
      if (!initial.segmentID) return // No stored custom segment, using defaults

      dispatch({type: 'FETCH_INIT'})
      try {
        const response = await api.segments.getSegment({
          clientID: client.ID,
          ID: initial.segmentID,
        })

        if (response.result === 'success' && response.data !== null) {
          if (!didCancel) {
            dispatch({
              type: 'FETCH_SUCCESS',
              payload: {
                data: response.data as CamperSegment,
              },
            })
          }
        } else {
          throw new Error('Unsuccessful Fetch Segments Response')
        }
      } catch (e) {
        if (e instanceof Error) {
          log.error(e)
        }

        if (!didCancel) {
          dispatch({type: 'FETCH_FAILURE'})
        }
      }
    }

    fetchData()

    return (): void => {
      didCancel = true
    }
  }, [initial.segmentID, client])

  const deleteSegment: SegmentManagerContext['deleteSegment'] = async (
    segmentID = state.segmentID,
  ) => {
    let response: {result: ReturnStatus} = {
      result: 'success',
    }

    // Only delete if there is a segment to delete
    if (segmentID) {
      response = await api.segments.deleteSegment({
        clientID: client.ID,
        ID: segmentID,
      })
    }

    return Promise.resolve({
      status: response.result,
    })
  }

  const createSegment: SegmentManagerContext['createSegment'] = async () => {
    // Defense-in-depth: the Apply button is gated on !allArraysAreEmpty in
    // SegmentStepUI, so this path should be unreachable from the UI. Any
    // programmatic caller that slips through still fails closed instead of
    // creating an empty "all campers" segment on the server.
    if (allArraysAreEmpty(state.segments)) {
      dispatch({type: 'CREATE_FAILURE'})
      return Promise.resolve({status: 'failure' as ReturnStatus, data: null})
    }

    dispatch({type: 'CREATE_INIT'})

    const response = await api.segments.upsertSegment({
      clientID: client.ID,
      segmentGroup: {
        ID: '0',
        // name: '', // These are anonymous segments
        type: CAMPER_PARENT_SEGMENT_TYPE,
        content: state.segments,
      },
    })

    if (response.result !== 'success') {
      dispatch({type: 'CREATE_FAILURE'})
      return Promise.resolve({status: 'failure' as ReturnStatus, data: null})
    }

    return Promise.resolve({
      status: 'success' as ReturnStatus,
      data: {segmentID: response.data.ID},
    })
  }

  const getSegmentCurrentSnapShot: SegmentManagerContext['getSegmentCurrentSnapShot'] =
    async (segmentID) => {
      let resolvedReturn: {
        status: ReturnStatus
        data: SegmentCurrentSnapshot | null
      }

      const response = await api.segments.getSegmentCurrentSnapShot({
        clientID: client.ID,
        seasonID: client.currentSeasonID.toString(),
        ID: segmentID,
      })

      if (response.result === 'success') {
        resolvedReturn = {status: 'success', data: response.data}
      } else {
        resolvedReturn = {status: 'failure', data: null} // failure
      }

      return Promise.resolve(resolvedReturn)
    }

  const value: SegmentManagerContext = {
    ...state,
    mode: initial.segmentID === undefined ? 'create' : 'edit',
    dispatch,
    createSegment,
    deleteSegment,
    getSegmentCurrentSnapShot,
  }

  return (
    <SegmentManagerContextProvider value={value}>
      {children}
    </SegmentManagerContextProvider>
  )
}

/* ***************************************************** */
export const useSegments = useSegmentManagerContext
